import type {
  Conversation,
  Media,
  Message,
  Participant,
  User,
} from "@twilio/conversations";
import type { ReduxConversation } from "./store/reducers/convoReducer";
import type {
  ReduxMedia,
  ReduxMessage,
} from "./store/reducers/messageListReducer";
import type { ReduxParticipant } from "./store/reducers/participantsReducer";
import type { ReduxUser } from "./store/reducers/userReducer";
import type { ConversationDto } from "./dto/conversation";

const USE_MOCKS = String(process.env.REACT_APP_USE_MOCKS ?? "false") === "true";

export const conversationsMap = new Map<string, Conversation>();
export const messagesMap = new Map<string, Message>();
export const usersMap = new Map<string, User>();
export const mediaMap = new Map<string, Media>();
export const participantsMap = new Map<string, Participant>();

const capitalize = (string: string): string =>
  `${string[0].toUpperCase()}${string.substring(1)}`;

const makeMockConversation = (sid: string): Conversation => {
  let lastIndex = 100;
  const conv: Partial<Conversation> = {
    sid,
    friendlyName: `Mock ${sid}`,
    dateUpdated: new Date(),
    lastReadMessageIndex: 0,
    notificationLevel: "default" as any,
    lastMessage: undefined,
    typing: async () => {
      // no-op
    },
    prepareMessage: () => {
      let body = "";
      const media: FormData[] = [];

      const builder = {
        setBody: (text: string) => {
          body = text;
          return builder;
        },
        addMedia: (fd: FormData) => {
          media.push(fd);
          return builder;
        },
        build: () => ({
          send: async () => {
            lastIndex += 1;

            (conv as any).lastMessage = {
              index: lastIndex,
              dateCreated: new Date(),
            };

            return lastIndex;
          },
        }),
      };

      return builder as any; // Twilio types sono complessi, mock minimale
    },
    updateFriendlyName: async (newName: string) => {
      (conv as any).friendlyName = newName;
      (conv as any).dateUpdated = new Date();
      return conv as Conversation;
    },

    getUnreadMessagesCount: async () => 0,

    advanceLastReadMessageIndex: async (idx: number) => {
      (conv as any).lastReadMessageIndex = idx;
      return idx;
    },
  };

  return conv as Conversation;
};

const makeMockMessage = (sid: string): Message => {
  const msg: Partial<Message> = {
    sid,
    body: "",
    author: "mock@author",
    index: 0,
    dateCreated: new Date(),
    attributes: {},

    updateBody: async (text: string) => {
      (msg as any).body = text;
      return msg as Message;
    },

    updateAttributes: async (attrs: any) => {
      (msg as any).attributes = attrs;
      return msg as Message;
    },
  };

  return msg as Message;
};

const makeMockUser = (identity: string): User => {
  const u: Partial<User> = {
    identity,
    friendlyName: identity,
  };
  return u as User;
};

const makeMockMedia = (sid: string): Media => {
  const m: Partial<Media> = {
    sid,
    filename: `file-${sid}`,
    contentType: "application/octet-stream",
    size: 0,
  };
  return m as Media;
};

const makeMockParticipant = (sid: string): Participant => {
  const p: Partial<Participant> = {
    sid,
    identity: `user-${sid}`,
  };
  return p as Participant;
};

const getSdkObject = <T>(
  objectMap: Map<string, T>,
  sid: string,
  type: "conversation" | "message" | "media" | "participant" | "user",
  mockFactory?: (sid: string) => T
): T => {
  const existing = objectMap.get(sid);
  if (existing) return existing;

  if (USE_MOCKS && mockFactory) {
    const mock = mockFactory(sid);
    objectMap.set(sid, mock);
    return mock;
  }

  throw new Error(`${capitalize(type)} with SID ${sid} was not found.`);
};

export const getSdkConversationObject = (
  reduxConversation: ReduxConversation
): Conversation =>
  getSdkObject(
    conversationsMap,
    reduxConversation.sid,
    "conversation",
    makeMockConversation
  );

export const getNewSdkConversationObject = (
  conversation: ConversationDto
): Conversation =>
  getSdkObject(
    conversationsMap,
    conversation.twilioSid,
    "conversation",
    makeMockConversation
  );

export const getSdkMessageObject = (reduxMessage: ReduxMessage): Message =>
  getSdkObject(messagesMap, reduxMessage.sid, "message", makeMockMessage);

export const getSdkUserObject = (reduxUser: ReduxUser): User =>
  getSdkObject(usersMap, reduxUser.identity, "user", makeMockUser);

export const getSdkMediaObject = (reduxMedia: ReduxMedia): Media =>
  getSdkObject(mediaMap, reduxMedia.sid, "media", makeMockMedia);

export const getSdkParticipantObject = (
  reduxParticipant: ReduxParticipant
): Participant =>
  getSdkObject(
    participantsMap,
    reduxParticipant.sid,
    "participant",
    makeMockParticipant
  );
